import { ChargerDtoV2, ConnectorV2 } from '../component/charger/dto/charger.dto';
import { ConnectorStatus } from '../types/connector-status.enum';

/**
 * Charger Status Utility Functions
 *
 * Centralized logic for determining charger status and availability
 * to be reused across components and services.
 */

export enum ChargerStatus {
  AVAILABLE = 'AVAILABLE',
  IN_SESSION = 'IN_SESSION',
  UNAVAILABLE = 'UNAVAILABLE',
  MAINTENANCE = 'MAINTENANCE',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Check if any connector is available for charging
 * Takes into account maintenance mode and uses statusWithoutMaintenance when needed
 */
export const isAnyConnectorAvailable = (
  ports: ConnectorV2[],
  maintenanceFlag: boolean,
): boolean => {
  return (
    ports?.some((port) =>
      maintenanceFlag
        ? port.statusWithoutMaintenance?.toLowerCase() === ConnectorStatus.AVAILABLE.toLowerCase()
        : port.status.toLowerCase() === ConnectorStatus.AVAILABLE.toLowerCase(),
    ) || false
  );
};

/**
 * Check if charger has a single connector
 */
export const isSingleConnector = (ports: ConnectorV2[]): boolean => {
  return ports?.length === 1 || false;
};

/**
 * Check if any port has an active session
 */
export const hasActiveSession = (ports: ConnectorV2[]): boolean => {
  return (
    ports?.some((port) => port.status?.toLowerCase() === ConnectorStatus.SESSION.toLowerCase()) ||
    false
  );
};

/**
 * Get overall charger status based on port statuses and maintenance flag
 * This consolidates the logic used across different components
 */
export const getChargerStatus = (charger: ChargerDtoV2): ChargerStatus => {
  if (!charger.ports || charger.ports.length === 0) {
    return ChargerStatus.UNKNOWN;
  }

  // Check for active sessions first
  if (hasActiveSession(charger.ports)) {
    return ChargerStatus.IN_SESSION;
  }

  // Check if any connector is available
  if (isAnyConnectorAvailable(charger.ports, charger.maintenanceFlag)) {
    return ChargerStatus.AVAILABLE;
  }

  // If in maintenance mode and no connectors available
  if (charger.maintenanceFlag) {
    return ChargerStatus.MAINTENANCE;
  }

  // Default to unavailable
  return ChargerStatus.UNAVAILABLE;
};
